import React from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import BackendAxios from "../BackendAxios";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import {
  PDF_PAGE_IMAGE_OPTIONS,
  renderPdfPagesToImageFiles,
} from "../Utils/pdfPageImages";

function moveItem(array, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return array;
  }

  const nextArray = [...array];
  const [movedItem] = nextArray.splice(fromIndex, 1);
  nextArray.splice(toIndex, 0, movedItem);
  return nextArray;
}

function isPdfAssetUrl(url) {
  return /\.pdf([?#].*)?$/i.test(String(url || "").trim());
}

function getEntryPreviewImage(entry) {
  const images = Array.isArray(entry?.images) ? entry.images : [];
  if (images.length === 0) return null;

  return (
    images.find(
      (image) =>
        String(image?.type || "").trim().toLowerCase() === "cover" &&
        (image?.url || image?.previewUrl)
    ) ||
    images.find((image) => image?.url || image?.previewUrl) ||
    null
  );
}

class EntriesManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      entries: [],
      fields: [],
      fieldValueOptions: {},
      page: 0,
      rowsPerPage: 10,
      total: 0,
      loading: false,
      error: null,
      dialogOpen: false,
      editingEntry: null,
      entryName: "",
      existingImages: [],
      imageFiles: [],
      imageTypes: {},
      imagePreviews: {},
      fieldValues: [], // Array of { fieldId, value }
      successMessage: null,
      deleteTargetEntry: null,
      processingPdfUploads: false,
      draggedEntryId: null,
      orderConfirmOpen: false,
    };
  }

  componentDidMount() {
    this.fetchEntries();
    this.fetchFields();
    this.fetchFieldValueOptions();
  }

  fetchFields = async () => {
    try {
      const response = await BackendAxios.get("/fields");
      this.setState({ fields: response.data });
    } catch (error) {
      console.error("Failed to fetch fields:", error);
    }
  };

  fetchFieldValueOptions = async () => {
    try {
      const response = await BackendAxios.get("/entries/field-value-options");
      this.setState({ fieldValueOptions: response.data.fieldValueOptions || {} });
    } catch (error) {
      console.error("Failed to fetch field value options:", error);
    }
  };

  fetchEntries = async () => {
    this.setState({ loading: true, error: null });
    try {
      const { page, rowsPerPage } = this.state;
      const response = await BackendAxios.get("/entries", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
        },
      });

      this.setState({
        entries: response.data.entries || [],
        total: response.data.pagination?.total || 0,
        loading: false,
      });
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to fetch entries",
        loading: false,
      });
    }
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage }, () => {
      this.fetchEntries();
    });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState(
      { rowsPerPage: parseInt(event.target.value, 10), page: 0 },
      () => {
        this.fetchEntries();
      }
    );
  };

  handleOpenDialog = (entry = null) => {
    if (entry) {
      // Convert entry fieldValues to our format
      const fieldValues = (entry.fieldValues || []).map((fv) => ({
        fieldId: typeof fv.fieldId === "object" ? fv.fieldId._id : fv.fieldId,
        value: fv.value,
      }));
      
      this.setState({
        editingEntry: entry,
        entryName: entry.name,
        existingImages: [...(entry.images || [])],
        imageFiles: [],
        imageTypes: {},
        imagePreviews: {},
        fieldValues: fieldValues,
        dialogOpen: true,
      });
    } else {
      this.setState({
        editingEntry: null,
        entryName: "",
        existingImages: [],
        imageFiles: [],
        imageTypes: {},
        imagePreviews: {},
        fieldValues: [],
        dialogOpen: true,
      });
    }
  };

  handleCloseDialog = () => {
    this.setState({
      dialogOpen: false,
      editingEntry: null,
      entryName: "",
      existingImages: [],
      imageFiles: [],
      imageTypes: {},
      imagePreviews: {},
      fieldValues: [],
      error: null,
      successMessage: null,
    });
  };

  handleNameChange = (event) => {
    this.setState({ entryName: event.target.value });
  };

  handleImageAdd = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) {
      return;
    }

    this.setState({ processingPdfUploads: true, error: null });
    const expandedFiles = [];

    try {
      for (const file of files) {
        const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
        if (isPdf) {
          const pageFiles = await renderPdfPagesToImageFiles(
            file,
            PDF_PAGE_IMAGE_OPTIONS
          );
          expandedFiles.push(...pageFiles);
        } else {
          expandedFiles.push(file);
        }
      }

      const newImageFiles = [...this.state.imageFiles, ...expandedFiles];
      const newImageTypes = { ...this.state.imageTypes };
      const newImagePreviews = { ...this.state.imagePreviews };
      const startIdx = this.state.imageFiles.length;

      expandedFiles.forEach((file, idx) => {
        newImageTypes[startIdx + idx] = newImageTypes[startIdx + idx] || "default";
        newImagePreviews[startIdx + idx] = "";
      });

      this.setState({
        imageFiles: newImageFiles,
        imageTypes: newImageTypes,
        imagePreviews: newImagePreviews,
        processingPdfUploads: false,
      });
    } catch (error) {
      this.setState({
        error: error.message || "Failed to process one or more PDF files",
        processingPdfUploads: false,
      });
    } finally {
      event.target.value = "";
    }
  };

  handleImageRemove = (index) => {
    const newImageFiles = this.state.imageFiles.filter((_, i) => i !== index);
    const newImageTypes = { ...this.state.imageTypes };
    const newImagePreviews = { ...this.state.imagePreviews };
    delete newImageTypes[index];
    delete newImagePreviews[index];

    // Reindex remaining types (preserve user-defined types)
    const reindexedTypes = {};
    const reindexedPreviews = {};
    newImageFiles.forEach((file, i) => {
      // Find the original index of this file
      const originalIndex = this.state.imageFiles.findIndex((f) => f === file);
      if (originalIndex !== -1 && newImageTypes[originalIndex] !== undefined) {
        reindexedTypes[i] = newImageTypes[originalIndex];
      }
      if (originalIndex !== -1 && newImagePreviews[originalIndex] !== undefined) {
        reindexedPreviews[i] = newImagePreviews[originalIndex];
      }
    });

    this.setState({
      imageFiles: newImageFiles,
      imageTypes: reindexedTypes,
      imagePreviews: reindexedPreviews,
    });
  };

  handleImageTypeChange = (index, type) => {
    this.setState({
      imageTypes: {
        ...this.state.imageTypes,
        [index]: type,
      },
    });
  };

  handleAddField = () => {
    this.setState({
      fieldValues: [...this.state.fieldValues, { fieldId: "", value: "" }],
    });
  };

  handleRemoveField = (index) => {
    const newFieldValues = this.state.fieldValues.filter((_, i) => i !== index);
    this.setState({ fieldValues: newFieldValues });
  };

  handleFieldChange = (index, fieldId) => {
    const { fieldValues, fields } = this.state;
    
    // Check if this field is already used in another fieldValue
    const isDuplicate = fieldValues.some((fv, i) => i !== index && fv.fieldId === fieldId && fieldId !== "");
    
    if (isDuplicate) {
      this.setState({ error: "This field has already been added to this entry" });
      return;
    }
    
    const newFieldValues = [...fieldValues];
    const selectedField = fields.find((field) => field._id === fieldId);
    const nextValue =
      selectedField?.type === "boolean"
        ? true
        : newFieldValues[index]?.value === true
        ? ""
        : newFieldValues[index]?.value ?? "";

    newFieldValues[index] = {
      ...newFieldValues[index],
      fieldId,
      value: nextValue,
    };
    this.setState({ fieldValues: newFieldValues, error: null });
  };

  handleFieldValueChange = (index, value) => {
    const newFieldValues = [...this.state.fieldValues];
    newFieldValues[index] = { ...newFieldValues[index], value };
    this.setState({ fieldValues: newFieldValues });
  };


  handleSave = async () => {
    const { entryName, imageFiles, imageTypes, imagePreviews, editingEntry } =
      this.state;

    if (!entryName.trim()) {
      this.setState({ error: "Entry name is required" });
      return;
    }

    const { existingImages } = this.state;
    if (imageFiles.length === 0 && existingImages.length === 0) {
      this.setState({ error: "At least one image is required" });
      return;
    }

    this.setState({ loading: true, error: null, successMessage: null });

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", entryName);
      
      // Append files
      imageFiles.forEach((file) => {
        formData.append("images", file);
      });
      
      // Append image types as JSON array
      const typesArray = imageFiles.map((_, idx) => imageTypes[idx] || "default");
      formData.append("imageTypes", JSON.stringify(typesArray));
      const previewsArray = imageFiles.map((_, idx) => imagePreviews[idx] || "");
      formData.append("imagePreviews", JSON.stringify(previewsArray));

      // Append field values
      const { fieldValues = [] } = this.state;
      const validFieldValues = (fieldValues || []).filter((fv) => fv && fv.fieldId && fv.value !== "");
      
      // Validate that fieldIds are unique
      const fieldIds = validFieldValues.map(fv => fv.fieldId);
      const uniqueFieldIds = [...new Set(fieldIds)];
      if (fieldIds.length !== uniqueFieldIds.length) {
        this.setState({ error: "Each field can only be used once per entry", loading: false });
        return;
      }
      
      formData.append("fieldValues", JSON.stringify(validFieldValues));

      if (editingEntry) {
        // Include existing images if editing
        formData.append("existingImages", JSON.stringify(existingImages));
        
        // Update entry
        await BackendAxios.put(`/entries/${editingEntry._id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.setState({
          successMessage: "Entry updated successfully",
        });
      } else {
        // Create entry
        await BackendAxios.post("/entries", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.setState({
          successMessage: "Entry created successfully",
        });
      }

      // Refresh entries list
      setTimeout(() => {
        this.fetchEntries();
        this.fetchFieldValueOptions();
        this.handleCloseDialog();
      }, 1000);
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to save entry",
        loading: false,
      });
    }
  };

  handleDelete = async () => {
    const entryId = this.state.deleteTargetEntry?._id;
    if (!entryId) {
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      await BackendAxios.delete(`/entries/${entryId}`);
      this.setState({ deleteTargetEntry: null });
      this.fetchEntries();
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to delete entry",
        loading: false,
        deleteTargetEntry: null,
      });
    }
  };

  handleEntryDragStart = (entryId) => {
    this.setState({ draggedEntryId: entryId });
  };

  handleEntryDragEnd = () => {
    this.setState({ draggedEntryId: null });
  };

  handleEntryDrop = (targetEntryId) => {
    this.setState((prevState) => {
      const fromIndex = prevState.entries.findIndex(
        (entry) => entry._id === prevState.draggedEntryId
      );
      const toIndex = prevState.entries.findIndex(
        (entry) => entry._id === targetEntryId
      );

      return {
        entries: moveItem(prevState.entries, fromIndex, toIndex),
        draggedEntryId: null,
      };
    });
  };

  handleSaveEntryOrderClick = () => {
    this.setState({ orderConfirmOpen: true, error: null, successMessage: null });
  };

  handleSaveEntryOrderCancel = () => {
    this.setState({ orderConfirmOpen: false });
  };

  handleSaveEntryOrderConfirm = async () => {
    this.setState({ loading: true, error: null, successMessage: null });

    try {
      const { entries, page, rowsPerPage } = this.state;
      const pageOffset = page * rowsPerPage;
      const entryOrder = entries.map((entry, index) => ({
        id: entry._id,
        displayOrder: pageOffset + index,
      }));

      await BackendAxios.put("/entries/order", {
        entryOrder,
      });

      this.setState(
        {
          loading: false,
          orderConfirmOpen: false,
          successMessage: "Entry order updated successfully",
        },
        this.fetchEntries
      );
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to update entry order",
        loading: false,
        orderConfirmOpen: false,
      });
    }
  };

  render() {
    const {
      entries,
      fields,
      page,
      rowsPerPage,
      total,
      loading,
      error,
      dialogOpen,
      entryName,
      existingImages,
      imageFiles,
      imageTypes,
      imagePreviews,
      fieldValues,
      fieldValueOptions,
      editingEntry,
      successMessage,
      deleteTargetEntry,
      processingPdfUploads,
      draggedEntryId,
      orderConfirmOpen,
    } = this.state;

    return (
      <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h5">Entries Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Drag entries to control the order they appear on Browse Designs.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.handleOpenDialog()}
            sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
          >
            Create Entry
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => this.setState({ successMessage: null })}
          >
            {successMessage}
          </Alert>
        )}

        <TableContainer 
          component={Paper} 
          sx={{ 
            width: "100%", 
            maxWidth: "100%", 
            overflowX: "auto", 
            boxSizing: "border-box"
          }}
        >
          <Table sx={{ width: "100%", tableLayout: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 72 }}>Order</TableCell>
                <TableCell sx={{ width: 88 }}>Preview</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Name</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Images</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Created</TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No entries found
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry, index) => {
                  const previewImage = getEntryPreviewImage(entry);
                  const previewSource = isPdfAssetUrl(previewImage?.url)
                    ? previewImage?.previewUrl || ""
                    : previewImage?.url || previewImage?.previewUrl || "";

                  return (
                    <TableRow
                      key={entry._id}
                      draggable
                      onDragStart={() => this.handleEntryDragStart(entry._id)}
                      onDragEnd={this.handleEntryDragEnd}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => this.handleEntryDrop(entry._id)}
                      sx={{
                        cursor: entries.length > 1 ? "grab" : "default",
                        backgroundColor:
                          draggedEntryId === entry._id
                            ? "rgba(91, 113, 149, 0.12)"
                            : "transparent",
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DragIndicatorIcon sx={{ color: "#7b8798", fontSize: 18 }} />
                          <Typography variant="body2" color="text.secondary">
                            {page * rowsPerPage + index + 1}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 64,
                            height: 48,
                            border: "1px solid #d5d9df",
                            backgroundColor: "#f5f7fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          {previewSource ? (
                            <Box
                              component="img"
                              src={previewSource}
                              alt={`${entry.name} preview`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : (
                            <ImageIcon sx={{ color: "#7b8798" }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{entry.name}</TableCell>
                      <TableCell>
                        <Chip
                          icon={<ImageIcon />}
                          label={`${entry.images?.length || 0} images`}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => this.handleOpenDialog(entry)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.setState({ deleteTargetEntry: entry })}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={this.handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Button
            variant="outlined"
            onClick={this.handleSaveEntryOrderClick}
            disabled={loading || entries.length < 2}
          >
            Save Entry Order
          </Button>
        </Box>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={this.handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editingEntry ? "Edit Entry" : "Create Entry"}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
              <TextField
                label="Entry Name"
                value={entryName}
                onChange={this.handleNameChange}
                fullWidth
                required
              />

              {editingEntry && existingImages && existingImages.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing Images:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {existingImages.map((img, idx) => (
                      <Chip
                        key={idx}
                        label={`Image ${idx + 1} (${img.type})`}
                        size="small"
                        onDelete={() => {
                          const newImages = existingImages.filter((_, i) => i !== idx);
                          this.setState({
                            existingImages: newImages,
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Add New Images/PDFs:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  PDF uploads are converted into one image per page.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Use image type <strong>cover</strong> for the image that should appear on
                  the main Browse Designs page.
                </Typography>
                <input
                  accept="image/*,application/pdf"
                  style={{ display: "none" }}
                  id="image-upload"
                  type="file"
                  multiple
                  onChange={this.handleImageAdd}
                />
                <label htmlFor="image-upload">
                  <Button variant="outlined" component="span" startIcon={<ImageIcon />}>
                    Upload Files
                  </Button>
                </label>
              </Box>

              {processingPdfUploads && (
                <Alert severity="info" icon={<CircularProgress size={18} />}>
                  Converting PDF pages. This can take a moment for large plan files.
                </Alert>
              )}

              {imageFiles.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Selected Files:
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {imageFiles.map((file, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1,
                          border: "1px solid #ddd",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {file.name}
                        </Typography>
                        <TextField
                          size="small"
                          label="Type"
                          placeholder="e.g., thumbnail, full-size"
                          value={imageTypes[idx] || ""}
                          onChange={(e) => this.handleImageTypeChange(idx, e.target.value)}
                          sx={{ width: 200 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => this.handleImageRemove(idx)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2">Fields:</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={this.handleAddField}
                  >
                    Add Field
                  </Button>
                </Box>
                {fieldValues.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {fieldValues.map((fv, idx) => {
                      const selectedField = fields.find((f) => f._id === fv.fieldId);
                      const fieldType = selectedField?.type || "text";
                      
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 1,
                            border: "1px solid #ddd",
                            borderRadius: 1,
                          }}
                        >
                          <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Field</InputLabel>
                            <Select
                              value={fv.fieldId || ""}
                              onChange={(e) => this.handleFieldChange(idx, e.target.value)}
                              label="Field"
                            >
                              {fields.map((field) => {
                                // Check if this field is already used in another fieldValue
                                const isUsed = fieldValues.some((otherFv, otherIdx) => 
                                  otherIdx !== idx && otherFv.fieldId === field._id
                                );
                                return (
                                  <MenuItem 
                                    key={field._id} 
                                    value={field._id}
                                    disabled={isUsed}
                                  >
                                    {field.name} {isUsed && "(already added)"}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                          {fieldType === "boolean" ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flex: 1, fontStyle: "italic" }}
                            >
                              Selecting this field sets it to true.
                            </Typography>
                          ) : fieldType === "text" ? (
                            <Autocomplete
                              freeSolo
                              options={fieldValueOptions[fv.fieldId] || []}
                              value={String(fv.value || "")}
                              inputValue={String(fv.value || "")}
                              onChange={(event, value) =>
                                this.handleFieldValueChange(idx, value || "")
                              }
                              onInputChange={(event, value) =>
                                this.handleFieldValueChange(idx, value)
                              }
                              sx={{ flex: 1 }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label="Value"
                                  placeholder="Select or type a new value"
                                />
                              )}
                            />
                          ) : (
                            <TextField
                              size="small"
                              label="Value"
                              type={
                                fieldType === "number" || fieldType === "squareFootage"
                                  ? "number"
                                  : fieldType === "date"
                                    ? "date"
                                    : "text"
                              }
                              value={fv.value || ""}
                              onChange={(e) => this.handleFieldValueChange(idx, e.target.value)}
                              sx={{ flex: 1 }}
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => this.handleRemoveField(idx)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {error && (
                <Alert severity="error" onClose={() => this.setState({ error: null })}>
                  {error}
                </Alert>
              )}

              {successMessage && (
                <Alert
                  severity="success"
                  onClose={() => this.setState({ successMessage: null })}
                >
                  {successMessage}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseDialog}>Cancel</Button>
            <Button
              onClick={this.handleSave}
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
            >
              {loading ? <CircularProgress size={24} /> : editingEntry ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(deleteTargetEntry)}
          onClose={() => this.setState({ deleteTargetEntry: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Entry</DialogTitle>
          <DialogContent>
            <Typography>
              Delete
              {" "}
              <strong>{deleteTargetEntry?.name || "this entry"}</strong>
              {" "}
              and all of its stored images?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.setState({ deleteTargetEntry: null })}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={this.handleDelete}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={orderConfirmOpen} onClose={this.handleSaveEntryOrderCancel}>
          <DialogTitle>Save Entry Order</DialogTitle>
          <DialogContent>
            <Typography>
              Save this entry order for Browse Designs?
            </Typography>
            <Typography sx={{ mt: 1 }} color="text.secondary">
              The paginated Browse Designs page will use this saved sequence.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleSaveEntryOrderCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={this.handleSaveEntryOrderConfirm}
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Save Order"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default EntriesManagement;

